/**
 * SSI Conflict Detection Utility
 * 
 * This utility supports the SSI-first workflow (e.g. 3 Borough / SLaM) where the
 * Semi-Structured Interview (SSI) is conducted by a junior clinician BEFORE the
 * Clinical Interview (CI) is done by the Lead clinician.
 * 
 * In this workflow, conflicts can arise when:
 * - SSI clinician recommends "Not Met" but Lead clinician's CI diagnosis indicates ADHD
 * - SSI clinician recommends "Fully Met/Partially Met" but Lead's CI diagnosis does not indicate ADHD
 * - Similar conflicts can occur at the Diagnostic Decision (DD) stage
 * 
 * The more common case is: SSI says "Not Met" (under-counted symptoms) but Lead says ADHD.
 * 
 * For hierarchical DF orgs, the CI-stage check compares against the Provisional
 * Primary Diagnosis (PPD).  For flat DF orgs (no PPD concept), it checks whether
 * ADHD is included in the "Conditions Under Consideration" list instead.
 * 
 * This utility provides functions to:
 * 1. Detect conflicts between SSI recommendations and CI/DD decisions
 * 2. Determine if conflict detection should be shown (org-specific)
 * 3. Get detailed conflict information for UI display
 *
 * Org detection is centralised via resolveWorkflowConfig so that new patients
 * with a stamped workflowConfig use it directly while legacy patients fall back
 * to deriveLegacyConfig (org-name string matching).
 */

import { 
    isADHDProvisionalPrimaryDiagnosis, 
    isADHDDiagnosisPositive, 
    isSSIRecommendationPositive,
    getSSIRecommendationDetails,
    getProvisionalPrimaryDiagnosis
} from './conditionChecks';
import { resolveWorkflowConfig } from '../hooks/useWorkflowConfig';

const ADHD_CONDITION_NAME = 'Attention Deficit Hyperactivity Disorder (ADHD)';

/**
 * Checks whether ADHD is among the "Conditions Under Consideration" in the
 * flat DF model.  Flat DF orgs store their conditions in the
 * `differentialDiagnoses` array on CI section 12 (the Diagnostic Formulation
 * section, identified by sectionVarName = 'diagnosticFormulation').
 *
 * @param {Object} patientDataModel - The patient data model
 * @returns {boolean} - True if ADHD is in the differentials list
 */
const isADHDInConditionsUnderConsideration = (patientDataModel) => {
    try {
        const sections = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections || {};
        const dfSection = Object.values(sections)
            .find(s => s?.sectionVarName === 'diagnosticFormulation');
        const differentials = dfSection?.differentialDiagnoses || [];
        return differentials.some(
            d => d?.toLowerCase() === ADHD_CONDITION_NAME.toLowerCase()
        );
    } catch {
        return false;
    }
};

/**
 * Checks if the organisation uses the SSI-first workflow (where conflict
 * detection is relevant).
 *
 * @deprecated Prefer `resolveWorkflowConfig(patientDataModel, claims).isSSIFirst`
 *   which honours patient-stamped config before falling back to org-name matching.
 *   Retained for backward compat with any external callers.
 *
 * @param {string} organisation - The organisation name
 * @returns {boolean} - True if organisation uses SSI-first workflow
 */
export const isSSIFirstOrganisation = (organisation) => {
    if (!organisation) return false;
    return resolveWorkflowConfig(null, { organisation }).isSSIFirst;
};

/**
 * Checks if the SSI module is completed (status = 'completed')
 * 
 * @param {Object} patientDataModel - The patient data model
 * @returns {boolean} - True if SSI is completed
 */
export const isSSICompleted = (patientDataModel) => {
    return patientDataModel?.dataModel?.['3_semiStructuredInterview']?.status === 'completed';
};

/**
 * Checks if the SSI report is approved
 * 
 * @param {Object} patientDataModel - The patient data model
 * @returns {boolean} - True if SSI report is approved
 */
export const isSSIReportApproved = (patientDataModel) => {
    return patientDataModel?.dataModel?.['6_manageReports']?.ssiReportApproved === true;
};

/**
 * Checks if the CI module is completed
 * 
 * @param {Object} patientDataModel - The patient data model
 * @returns {boolean} - True if CI is completed
 */
export const isCICompleted = (patientDataModel) => {
    return patientDataModel?.dataModel?.['2_clinicalInterview']?.status === 'completed';
};

/**
 * Checks if the DD module is completed
 * 
 * @param {Object} patientDataModel - The patient data model
 * @returns {boolean} - True if DD is completed
 */
export const isDDCompleted = (patientDataModel) => {
    return patientDataModel?.dataModel?.['4_diagnosticDecision']?.status === 'completed';
};

/**
 * Checks if SSI reset is currently required
 * 
 * @param {Object} patientDataModel - The patient data model
 * @returns {boolean} - True if SSI reset is required
 */
export const isSSIResetRequired = (patientDataModel) => {
    return patientDataModel?.dataModel?.['6_manageReports']?.ssiResetRequired === true;
};

/**
 * Gets the SSI clinician information
 * 
 * @param {Object} patientDataModel - The patient data model
 * @returns {Object|null} - SSI clinician info or null
 */
export const getSSIClinician = (patientDataModel) => {
    return patientDataModel?.clinicians?.clinicianSSI || null;
};

/**
 * Gets the Lead clinician information
 * 
 * @param {Object} patientDataModel - The patient data model
 * @returns {Object|null} - Lead clinician info or null
 */
export const getLeadClinician = (patientDataModel) => {
    return patientDataModel?.clinicians?.clinicianLead || null;
};

/**
 * Detects conflicts between SSI recommendations and CI/DD decisions.
 * 
 * This is the main conflict detection function that should be called from UI components.
 * It handles both conflict directions:
 * - SSI positive (Fully/Partially met), CI/DD negative (Not ADHD)
 * - SSI negative (Not met), CI/DD positive (ADHD) - THE MORE COMMON CASE
 * 
 * @param {Object} patientDataModel - The patient data model
 * @param {string} organisation - The organisation name (for org-specific gating)
 * @returns {Object} - Conflict detection result
 */
export const detectSSIConflict = (patientDataModel, organisation) => {
    // Default result - no conflict
    const noConflictResult = {
        hasConflict: false,
        conflictType: null,
        shouldShowConflictUI: false,
        ssiRecommendation: null,
        currentDecision: null,
        conflictDescription: null,
        ssiPositive: null,
        ciIsADHD: null,
        ddPositive: null,
        isCommonCase: false,  // SSI negative, Lead positive
    };

    // Only check for conflicts in SSI-first organisations.
    // resolveWorkflowConfig uses the patient's stamped config if available,
    // falling back to deriveLegacyConfig for legacy patients.
    const wf = resolveWorkflowConfig(patientDataModel, { organisation });
    if (!wf.isSSIFirst) {
        return noConflictResult;
    }

    // SSI Review patients (SLaM, TOC, etc.) do NOT produce a formal RDD
    // recommendation ("Fully Met" / "Not Met"). Without that data point,
    // conflict detection is meaningless and would produce false positives
    // (isSSIRecommendationPositive returns false when no RDD exists).
    if (wf.showSSIReviewPage) {
        return noConflictResult;
    }

    // SSI must be completed and approved for conflict to be relevant
    if (!isSSICompleted(patientDataModel) || !isSSIReportApproved(patientDataModel)) {
        return noConflictResult;
    }

    // If SSI reset is already required, don't show new conflict (already handled)
    if (isSSIResetRequired(patientDataModel)) {
        return {
            ...noConflictResult,
            hasConflict: false,
            ssiResetAlreadyRequired: true,
        };
    }

    const isFlatDF = wf.isFlatDifferentials;

    // Get the recommendation/decision values
    const ssiPositive = isSSIRecommendationPositive(patientDataModel);

    // For the CI-stage ADHD check, flat DF orgs have no PPD — they use a
    // flat "Conditions Under Consideration" list instead.  We check whether
    // ADHD appears in that list.  Hierarchical orgs use the PPD as before.
    const ciIsADHD = isFlatDF
        ? isADHDInConditionsUnderConsideration(patientDataModel)
        : isADHDProvisionalPrimaryDiagnosis(patientDataModel);

    const ddPositive = isADHDDiagnosisPositive(patientDataModel);
    
    const ciCompleted = isCICompleted(patientDataModel);
    const ddCompleted = isDDCompleted(patientDataModel);

    // Get SSI recommendation details for display
    const ssiDetails = getSSIRecommendationDetails(patientDataModel);
    const ssiRecommendationText = ssiDetails?.diagnosisDecision || 'Unknown';

    // Determine conflict at CI stage (Lead has completed CI but not yet DD)
    const hasCIConflict = ciCompleted && !ddCompleted && (ssiPositive !== ciIsADHD);

    // Determine conflict at DD stage (Lead has completed DD)
    const hasDDConflict = ddCompleted && (ssiPositive !== ddPositive);

    // If no conflict, return early
    if (!hasCIConflict && !hasDDConflict) {
        return noConflictResult;
    }

    // Determine the conflict type and description
    const conflictType = hasDDConflict ? 'DD' : 'CI';
    
    // Determine if this is the "common case" (SSI negative, Lead positive)
    // This is when SSI under-counted symptoms (Not Met) but Lead identifies ADHD
    const isCommonCase = !ssiPositive && (hasDDConflict ? ddPositive : ciIsADHD);

    // Build human-readable descriptions.
    // DD-stage language is model-agnostic (it talks about criteria, not PPD).
    // CI-stage language must distinguish flat vs hierarchical models.
    let currentDecision;
    let conflictDescription;

    if (conflictType === 'DD') {
        currentDecision = ddPositive ? 'ADHD criteria met' : 'ADHD criteria not met';
        if (isCommonCase) {
            conflictDescription = `The SSI clinician recommended "Not Met" for ADHD criteria, but you have determined that ADHD criteria are met. The approved SSI report paragraphs may not accurately reflect your clinical findings.`;
        } else {
            conflictDescription = `The SSI clinician recommended "${ssiRecommendationText}" for ADHD criteria, but you have determined that ADHD criteria are not met. The approved SSI report paragraphs may not accurately reflect your clinical findings.`;
        }
    } else if (isFlatDF) {
        // Flat DF: reference "Conditions Under Consideration" instead of PPD
        currentDecision = ciIsADHD
            ? 'ADHD included in Conditions Under Consideration'
            : 'ADHD not included in Conditions Under Consideration';
        if (isCommonCase) {
            conflictDescription = `The SSI clinician recommended "Not Met" for ADHD criteria, but ADHD is included in your Conditions Under Consideration. The approved SSI report paragraphs may not accurately reflect your clinical findings.`;
        } else {
            conflictDescription = `The SSI clinician recommended "${ssiRecommendationText}" for ADHD criteria, but ADHD is not included in your Conditions Under Consideration. The approved SSI report paragraphs may not accurately reflect your clinical findings.`;
        }
    } else {
        // Hierarchical DF: reference PPD
        const actualPPD = getProvisionalPrimaryDiagnosis(patientDataModel);
        currentDecision = actualPPD || (ciIsADHD ? 'Attention Deficit Hyperactivity Disorder (ADHD)' : 'Not ADHD');
        if (isCommonCase) {
            conflictDescription = `The SSI clinician recommended "Not Met" for ADHD criteria, but your provisional primary diagnosis is ADHD. The approved SSI report paragraphs may not accurately reflect your clinical findings.`;
        } else {
            conflictDescription = `The SSI clinician recommended "${ssiRecommendationText}" for ADHD criteria, but your provisional primary diagnosis is not ADHD. The approved SSI report paragraphs may not accurately reflect your clinical findings.`;
        }
    }

    return {
        hasConflict: true,
        conflictType,
        shouldShowConflictUI: true,
        ssiRecommendation: ssiRecommendationText,
        currentDecision,
        conflictDescription,
        ssiPositive,
        ciIsADHD,
        ddPositive,
        isCommonCase,
        isFlatDifferentials: isFlatDF,
        ssiDetails,
        ssiClinician: getSSIClinician(patientDataModel),
        leadClinician: getLeadClinician(patientDataModel),
    };
};

/**
 * Gets conflict detection result specifically for the CI Diagnostic Formulation page (12_DF.js)
 * 
 * This function checks if a conflict exists when the Lead clinician confirms their
 * diagnostic formulation in the Clinical Interview.
 *
 * For hierarchical DF orgs the comparison is against the Provisional Primary
 * Diagnosis (PPD).  For flat DF orgs it checks whether ADHD is among the
 * Conditions Under Consideration (the caller passes ADHD or null accordingly).
 * 
 * @param {Object} patientDataModel - The patient data model
 * @param {string} organisation - The organisation name
 * @param {string|null} provisionalDiagnosis - The PPD being set, or ADHD/null for flat DF
 * @param {Object} [wfOverride] - Optional resolved workflow config; resolved internally if omitted
 * @returns {Object} - Conflict detection result
 */
export const detectSSIConflictForCI = (patientDataModel, organisation, provisionalDiagnosis, wfOverride) => {
    // resolveWorkflowConfig honours patient-stamped config first.
    const wf = wfOverride || resolveWorkflowConfig(patientDataModel, { organisation });
    if (!wf.isSSIFirst) {
        return { hasConflict: false, shouldShowConflictUI: false };
    }

    // SSI Review patients have no formal RDD recommendation to conflict with.
    if (wf.showSSIReviewPage) {
        return { hasConflict: false, shouldShowConflictUI: false };
    }

    // SSI must be completed and approved
    if (!isSSICompleted(patientDataModel) || !isSSIReportApproved(patientDataModel)) {
        return { hasConflict: false, shouldShowConflictUI: false };
    }

    // If SSI reset is already required, don't show new conflict
    if (isSSIResetRequired(patientDataModel)) {
        return { hasConflict: false, shouldShowConflictUI: false, ssiResetAlreadyRequired: true };
    }

    const isFlatDF = wf.isFlatDifferentials;

    // Get SSI recommendation
    const ssiPositive = isSSIRecommendationPositive(patientDataModel);
    const ssiDetails = getSSIRecommendationDetails(patientDataModel);
    const ssiRecommendationText = ssiDetails?.diagnosisDecision || 'Unknown';

    // Check if the diagnosis being set indicates ADHD.
    // For hierarchical: provisionalDiagnosis is the PPD string.
    // For flat: caller passes ADHD string if ADHD is in the conditions list, null otherwise.
    const isSettingADHD = provisionalDiagnosis?.toLowerCase() === ADHD_CONDITION_NAME.toLowerCase();

    // Conflict exists if SSI recommendation differs from CI diagnosis direction
    const hasConflict = ssiPositive !== isSettingADHD;

    if (!hasConflict) {
        return { hasConflict: false, shouldShowConflictUI: false };
    }

    // Build the result with model-appropriate language
    const isCommonCase = !ssiPositive && isSettingADHD;
    let conflictDescription;
    let currentDecision;

    if (isFlatDF) {
        currentDecision = isSettingADHD
            ? 'ADHD included in Conditions Under Consideration'
            : 'ADHD not included in Conditions Under Consideration';
        if (isCommonCase) {
            conflictDescription = `The SSI clinician recommended "Not Met" for ADHD criteria, but ADHD is included in your Conditions Under Consideration. The approved SSI report paragraphs may contain language that doesn't align with a positive ADHD diagnosis.`;
        } else {
            conflictDescription = `The SSI clinician recommended "${ssiRecommendationText}" for ADHD criteria, but ADHD is not included in your Conditions Under Consideration. The approved SSI report paragraphs may contain language that doesn't align with this diagnosis.`;
        }
    } else {
        currentDecision = provisionalDiagnosis || (isSettingADHD ? 'ADHD' : 'Not ADHD');
        if (isCommonCase) {
            conflictDescription = `The SSI clinician recommended "Not Met" for ADHD criteria, but you are setting a provisional primary diagnosis of ADHD. The approved SSI report paragraphs may contain language that doesn't align with a positive ADHD diagnosis.`;
        } else {
            conflictDescription = `The SSI clinician recommended "${ssiRecommendationText}" for ADHD criteria, but you are setting a provisional primary diagnosis that is not ADHD. The approved SSI report paragraphs may contain language that doesn't align with this diagnosis.`;
        }
    }

    return {
        hasConflict: true,
        conflictType: 'CI',
        shouldShowConflictUI: true,
        ssiRecommendation: ssiRecommendationText,
        currentDecision,
        conflictDescription,
        ssiPositive,
        ciIsADHD: isSettingADHD,
        isCommonCase,
        isFlatDifferentials: isFlatDF,
        ssiDetails,
        ssiClinician: getSSIClinician(patientDataModel),
        leadClinician: getLeadClinician(patientDataModel),
    };
};
